import { useEffect, useState } from 'react';
import { ApiError, api } from '../api/client';
import type { CommandType, Device } from '../api/types';
import { formatAgo, useT } from '../i18n';
import { useToast } from './Toast';
import {
  IconShare,
  IconBell,
  IconAlert,
  IconClose,
  IconCrosshair,
  IconExternal,
  IconNav,
  IconPin,
  IconPower,
  IconShield,
  IconShieldOff,
} from './Icons';

interface Props {
  device: Device;
  userLocation: { latitude: number; longitude: number } | null;
  onLocateMe: () => void;
  onClose: () => void;
}

const EARTH_RADIUS_KM = 6371.0088;
/** Top of the gauge dial. Above this the needle just pins. */
const GAUGE_MAX_KPH = 180;

/** Straight-line distance, good enough for "how far is my car from me". */
function distanceKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

function SpeedGauge({ speedKph, course }: { speedKph: number; course: number }) {
  const { t } = useT();
  const radius = 31;
  const circumference = 2 * Math.PI * radius;
  const ratio = Math.min(1, Math.max(0, speedKph / GAUGE_MAX_KPH));
  const colour = speedKph > 4 ? 'var(--moving)' : 'var(--parked)';

  return (
    <div className="gauge">
      <div className="gauge-dial">
        <svg width="74" height="74" viewBox="0 0 74 74">
          <circle className="gauge-track" cx="37" cy="37" r={radius} strokeWidth="6" />
          <circle
            className="gauge-value"
            cx="37"
            cy="37"
            r={radius}
            strokeWidth="6"
            style={{
              stroke: colour,
              strokeDasharray: circumference,
              strokeDashoffset: circumference * (1 - ratio),
            }}
          />
        </svg>
        <div className="gauge-num">
          <div style={{ textAlign: 'center' }}>
            <b>{Math.round(speedKph)}</b>
            <br />
            <span>km/h</span>
          </div>
        </div>
      </div>

      <div className="gauge-side">
        <div className="compass">
          <IconNav size={15} className="" />
          <span style={{ display: 'inline-flex', gap: 4 }}>
            {t('course')}
            <strong style={{ color: 'var(--text)' }}>{Math.round(course)}°</strong>
          </span>
        </div>
        <div
          className="compass"
          style={{ transform: `rotate(0deg)` }}
          aria-hidden="true"
          title={`${Math.round(course)}°`}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" style={{ transform: `rotate(${course}deg)` }}>
            <path d="M12 3 L18 20 L12 16.5 L6 20 Z" fill="currentColor" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function StateChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'good' | 'bad' | 'warn';
}) {
  return (
    <div className={`state-chip${tone ? ` ${tone}` : ''}`}>
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </div>
  );
}

export function DevicePanel({ device, userLocation, onLocateMe, onClose }: Props) {
  const { t, lang } = useT();
  const toast = useToast();
  const [busy, setBusy] = useState<CommandType | null>(null);
  const [notify, setNotify] = useState(device.notify !== false);
  const [savingNotify, setSavingNotify] = useState(false);

  // The switch follows the vehicle when the selection changes, or it would keep
  // showing the previous vehicle's setting.
  useEffect(() => setNotify(device.notify !== false), [device.id, device.notify]);

  const toggleNotify = async (next: boolean) => {
    setNotify(next); // optimistic: the switch must not lag the finger
    setSavingNotify(true);
    try {
      await api.setNotify(device.id, next);
    } catch (cause) {
      setNotify(!next); // put it back, and say why
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    } finally {
      setSavingNotify(false);
    }
  };

  const [confirmCut, setConfirmCut] = useState(false);

  // Drop the confirm dialog when the user switches vehicle.
  useEffect(() => setConfirmCut(false), [device.id]);

  const send = async (type: CommandType) => {
    setBusy(type);
    try {
      const result = await api.sendCommand(device.id, type);
      toast(result.queued ? 'info' : 'ok', result.queued ? t('queuedOffline') : t('commandSent'));
    } catch (cause) {
      // The server's refusal names the actual speed, so surface it verbatim.
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    } finally {
      setBusy(null);
      setConfirmCut(false);
    }
  };

  const position = device.position;
  const blocked = device.status.blocked === true;
  const armed = device.status.armed === true;
  const gap =
    position && userLocation
      ? distanceKm(
          userLocation.latitude,
          userLocation.longitude,
          position.latitude,
          position.longitude,
        )
      : null;

  return (
    <aside className="device-panel">
      <div className="panel-head">
        <div style={{ minWidth: 0 }}>
          <h2>{device.name}</h2>
          <p className="muted tiny" style={{ margin: 0 }}>
            {device.model} · {device.plate ?? device.imei}
          </p>
        </div>
        <span className={device.online ? 'pill online' : 'pill offline'}>
          {device.online ? t('online') : t('offline')}
        </span>
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('close')}>
          <IconClose />
        </button>
      </div>

      {!position ? (
        <p className="muted small">{t('noPosition')}</p>
      ) : (
        <>
          <SpeedGauge speedKph={position.speedKph} course={position.course} />

          <div className="chip-grid">
            <StateChip
              label={t('ignition')}
              value={
                device.status.ignition === null
                  ? t('unknown')
                  : device.status.ignition
                    ? t('on')
                    : t('off')
              }
              tone={device.status.ignition ? 'good' : undefined}
            />
            <StateChip
              label={t('engine')}
              value={device.status.blocked === null ? t('unknown') : blocked ? t('cut') : t('running')}
              tone={blocked ? 'bad' : device.status.blocked === false ? 'good' : undefined}
            />
            <StateChip
              label={t('alarm')}
              value={
                device.status.armed === null ? t('unknown') : armed ? t('armed') : t('disarmed')
              }
              tone={armed ? 'good' : undefined}
            />
            <StateChip
              label={t('lastUpdate')}
              value={formatAgo(device.lastSeenAt, lang)}
              tone={device.online ? undefined : 'warn'}
            />
          </div>

          {(device.status.batteryLevel !== null || device.status.gsmSignal !== null) && (
            <div className="chip-grid">
              {device.status.batteryLevel !== null && (
                <div className="state-chip">
                  <span className="label">{t('battery')}</span>
                  <span className="value">{device.status.batteryLevel}/6</span>
                  <span className="meter">
                    <i style={{ width: `${(device.status.batteryLevel / 6) * 100}%` }} />
                  </span>
                </div>
              )}
              {device.status.gsmSignal !== null && (
                <div className="state-chip">
                  <span className="label">{t('signal')}</span>
                  <span className="value">{device.status.gsmSignal}/4</span>
                  <span className="meter">
                    <i style={{ width: `${(device.status.gsmSignal / 4) * 100}%` }} />
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="btn-grid">
            <a
              className="btn"
              href={position.googleMapsUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              <IconExternal />
              {t('openInGoogleMaps')}
            </a>
            <button type="button" className="btn" onClick={onLocateMe}>
              <IconPin />
              {t('myLocation')}
            </button>
          </div>

          {gap !== null && (
            <div className="distance-box">
              <span className="small">
                {t('distanceToCar')} <strong>{gap.toFixed(1)} km</strong>
              </span>
              <a
                className="link-btn"
                href={`https://www.google.com/maps/dir/?api=1&origin=${userLocation!.latitude},${userLocation!.longitude}&destination=${position.latitude},${position.longitude}`}
                target="_blank"
                rel="noreferrer noopener"
              >
                {t('directions')}
              </a>
            </div>
          )}
        </>
      )}

      <span className="section-label">{t('engine')}</span>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {device.hasImmobiliser && (
          <>
            {blocked && <p className="form-error">{t('engineIsCut')}</p>}
            {device.status.blocked === null && (
              <p className="muted tiny">{t('engineStateUnknown')}</p>
            )}

            {/*
              Both commands are always available, never just the one that the
              reported state suggests. GT06 clones disagree on the status byte,
              so a device can be immobilised while reporting otherwise — and a
              customer stranded by that must still have a way to restore power.
            */}
            <div className="btn-grid">
              <button
                type="button"
                className={blocked ? 'btn' : 'btn danger'}
                disabled={busy !== null}
                onClick={() => setConfirmCut(true)}
              >
                {busy === 'engineStop' ? <span className="btn-spinner" /> : <IconPower />}
                {t('stopEngine')}
              </button>
              <button
                type="button"
                className={blocked ? 'btn success' : 'btn'}
                disabled={busy !== null}
                onClick={() => send('engineResume')}
              >
                {busy === 'engineResume' ? <span className="btn-spinner" /> : <IconPower />}
                {t('resumeEngine')}
              </button>
            </div>
          </>
        )}

        <div className="btn-grid">
          <button
            type="button"
            className="btn"
            disabled={busy !== null}
            onClick={() => send('alarmArm')}
          >
            <IconShield />
            {t('arm')}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy !== null}
            onClick={() => send('alarmDisarm')}
          >
            <IconShieldOff />
            {t('disarm')}
          </button>
        </div>

        {/*
          Alerts for this vehicle only. Stored per grant, so silencing a van
          parked in the yard does not silence the rest of the fleet. Hidden
          entirely when the caller holds no grant — an admin looking at a
          vehicle they do not own has nothing to store a preference against.
        */}
        {device.notify !== undefined && device.notify !== null && (
          <label className="notify-row">
            <input
              type="checkbox"
              checked={notify}
              disabled={savingNotify}
              onChange={(event) => toggleNotify(event.target.checked)}
            />
            <span>
              <IconBell />
              {t('notifyThisVehicle')}
            </span>
          </label>
        )}

        <ForwardRow device={device} />

        {/*
          A command to the tracker, not a map control. It asks the device to
          report immediately rather than waiting out its reporting interval,
          which is what someone standing in a car park actually wants.
        */}
        <button
          type="button"
          className="btn block"
          disabled={busy !== null}
          title={t('locateHint')}
          onClick={() => send('positionSingle')}
        >
          {busy === 'positionSingle' ? <span className="btn-spinner" /> : <IconCrosshair />}
          {t('locate')}
        </button>
        <p className="muted tiny" style={{ margin: '4px 0 0' }}>
          {t('locateHint')}
        </p>
      </div>

      {confirmCut && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-icon">
              <IconAlert size={22} />
            </div>
            <h3>{t('confirmCutTitle')}</h3>
            <p className="muted small" style={{ margin: 0 }}>
              {t('confirmCutBody')}
            </p>
            <div className="state-chip">
              <span className="label">{device.name}</span>
              <span className="value">{Math.round(position?.speedKph ?? 0)} km/h</span>
            </div>
            <div className="btn-grid">
              <button type="button" className="btn danger" onClick={() => send('engineStop')}>
                {t('confirm')}
              </button>
              <button type="button" className="btn" onClick={() => setConfirmCut(false)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

/**
 * Copy this vehicle's raw traffic to a second server.
 *
 * Folded away unless it is switched on: almost nobody needs it, and a host and
 * port field beside the engine controls would be a puzzle for everyone who does
 * not. Anyone with access to the vehicle may set it — a customer feeding their
 * own tracker to their own system is the case it exists for.
 */
function ForwardRow({ device }: { device: Device }) {
  const { t } = useT();
  const toast = useToast();

  const current = device.forwarding ?? null;
  const [open, setOpen] = useState(current !== null);
  const [host, setHost] = useState(current?.host ?? '');
  const [port, setPort] = useState(current ? String(current.port) : '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const next = device.forwarding ?? null;
    setHost(next?.host ?? '');
    setPort(next ? String(next.port) : '');
    setOpen(next !== null);
  }, [device.id, device.forwarding]);

  const save = async (target: { host: string; port: number } | null) => {
    setBusy(true);
    try {
      await api.setForward(device.id, target);
      // Not "saved": nothing changes until the tracker next connects, and
      // saying otherwise sends someone looking for data that is not coming yet.
      toast('ok', target ? t('forwardSaved') : t('forwardOff'));
    } catch (cause) {
      // The server explains a refused address in words — "resolves to loopback"
      // is worth showing verbatim.
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="forward-box">
      <button type="button" className="link-btn" onClick={() => setOpen((v) => !v)}>
        <IconShare />
        {t('forwardTitle')}
        {current && <span className="pill-on">{current.host}:{current.port}</span>}
      </button>

      {open && (
        <>
          <p className="muted tiny" style={{ margin: '6px 0 8px' }}>{t('forwardHint')}</p>
          <div className="forward-fields">
            <input
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder={t('forwardHost')}
              dir="ltr"
              disabled={busy}
            />
            <input
              value={port}
              onChange={(e) => setPort(e.target.value.replace(/\D/g, ''))}
              placeholder={t('forwardPort')}
              dir="ltr"
              inputMode="numeric"
              disabled={busy}
            />
          </div>
          <div className="btn-grid" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="btn primary"
              disabled={busy || !host.trim() || !port}
              onClick={() => save({ host: host.trim(), port: Number(port) })}
            >
              {t('save')}
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy || !current}
              onClick={() => {
                setHost('');
                setPort('');
                save(null);
              }}
            >
              {t('forwardStop')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
